import { getStoredLanguage } from "./language";
import { waitForAuthReady, isAuthReady } from "../auth/authReadiness";
import { 
  extractCsrfToken, 
  storeCsrfToken, 
  getCsrfToken, 
  requiresCsrfToken,
  clearCsrfToken 
} from "./csrf";
/**
 * Language Handling:
 * - Persists user preference in localStorage via language.ts (key: ps_lang)
 * - Automatically appends ?lang=<code> to every API request if absent
 * - Sends X-User-Language header for backend resolution
 * - To change language in UI, call changeLanguage('es') and refetch or rely on React Query key including lang
 */

const getUserLanguage = (): string => getStoredLanguage();

// Utility function to make authenticated API requests with CSRF protection
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
) => {
  // If auth not yet ready, wait (non-blocking for truly public endpoints can opt-out via header flag skip-auth-wait)
  const skipWait =
    (options as RequestInit & { skipAuthWait?: boolean })?.skipAuthWait === true ||
    (options.headers && (options.headers as Record<string, string>)["x-skip-auth-wait"]);
  if (!skipWait && !isAuthReady()) {
    await waitForAuthReady();
  }
  
  // In development, use relative URLs to leverage Vite's proxy
  // In production, build full URL using VITE_API_URL
  const isDevelopment = import.meta.env.DEV;
  const fullBase = isDevelopment
    ? url
    : url.startsWith("http")
    ? url
    : `${import.meta.env.VITE_API_URL}${url}`;
  const userLang = getUserLanguage();
  const separator = fullBase.includes("?") ? "&" : "?";
  const fullUrl = /[?&]lang=/.test(fullBase)
    ? fullBase
    : `${fullBase}${separator}lang=${userLang}`;

  const method = options.method || 'GET';
  
  // Check if this request needs CSRF protection
  const needsCsrfToken = requiresCsrfToken(method);
  const csrfToken = needsCsrfToken ? getCsrfToken() : null;

  const baseHeaders: HeadersInit = {
    "Content-Type": "application/json",
    "X-User-Language": userLang,
    ...options.headers,
  };

  // Add CSRF token to headers if needed
  if (needsCsrfToken && csrfToken) {
    (baseHeaders as Record<string, string>)["X-CSRF-Token"] = csrfToken;
  }

  // Cookie-first authentication to avoid stale Authorization header issues
  try {
    let cookieResponse = await fetch(fullUrl, {
      ...options,
      credentials: "include",
      headers: baseHeaders,
    });

    // Handle CSRF token extraction from successful responses
    if (cookieResponse.ok) {
      const responseCsrfToken = extractCsrfToken(cookieResponse);
      if (responseCsrfToken) {
        storeCsrfToken(responseCsrfToken);
      }
    }

    // Handle CSRF token validation errors
    if (cookieResponse.status === 403) {
      // Clone the response to read it without consuming the original
      const responseClone = cookieResponse.clone();
      try {
        const responseText = await responseClone.text();
        const errorData = JSON.parse(responseText);
        if (errorData.code === 'CSRF_TOKEN_INVALID') {
          // Clear invalid token and retry once
          clearCsrfToken();
          
          // For state-changing requests, try to get a fresh token first
          if (needsCsrfToken) {
            // Make a GET request to get a fresh CSRF token from the dedicated endpoint
            const tokenUrl = isDevelopment 
              ? '/api/csrf/token'
              : `${import.meta.env.VITE_API_URL}/api/csrf/token`;
            const tokenResponse = await fetch(tokenUrl, {
              method: 'GET',
              credentials: 'include',
              headers: {
                "X-User-Language": userLang,
              },
            });
            
            if (tokenResponse.ok) {
              const freshToken = extractCsrfToken(tokenResponse);
              if (freshToken) {
                storeCsrfToken(freshToken);
                // Retry the original request with the fresh token
                (baseHeaders as Record<string, string>)["X-CSRF-Token"] = freshToken;
                cookieResponse = await fetch(fullUrl, {
                  ...options,
                  credentials: "include",
                  headers: baseHeaders,
                });
              }
            }
          }
        }
      } catch (parseError) {
        // If we can't parse the error, just return the original response
      }
    }

    // Early 401 before readiness fully established: wait once then retry
    if (cookieResponse.status === 401 && !skipWait && !isAuthReady()) {
      await waitForAuthReady();
      cookieResponse = await fetch(fullUrl, {
        ...options,
        credentials: "include",
        headers: baseHeaders,
      });
    }

    return cookieResponse;
  } catch (error) {
    throw new Error(
      `Failed to fetch ${url}: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
};
